import { useState, useRef, useCallback } from "react";
import useWebSocket from "react-use-websocket";
import config from "environment";
import { AgentResponseMessage } from "winslow-core";

export type AIStreamResponse = {
  sources?: string[];
  personaIds?: string[];
  content: string;
  stage:
    | "starting"
    | "complete"
    | "streaming"
    | "status"
    | "render"
    | "display-asset"
    | "base-starting"
    | "base-streaming"
    | "base-complete"
    | "canvas-starting"
    | "canvas-streaming"
    | "canvas-complete"
    | "update-message";
  message?: string;
  success?: boolean;
  data?: any;
  savedMessage?: AgentResponseMessage;
};

export type UseAIStreamProps = {
  onStreamStart?: () => void;
  onStreamComplete?: (response: AIStreamResponse) => void;
  onStreamError?: (error: any) => void;
};

export const useAIStream = ({
  onStreamStart,
  onStreamComplete,
  onStreamError,
}: UseAIStreamProps = {}) => {
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [currentResponse, setCurrentResponse] = useState<string>("");
  const responseRef = useRef<string>("");

  const { sendJsonMessage, lastJsonMessage } = useWebSocket<AIStreamResponse>(
    config.wsAiUrl,
    {
      onOpen: () => {
        console.log("[useAIStream] WebSocket connection opened");
      },
      onClose: (code: any) => {
        console.log(
          "[useAIStream] WebSocket connection closed with code:",
          code
        );
      },
      shouldReconnect: (closeEvent) => {
        console.log(
          "[useAIStream] WebSocket reconnection attempt:",
          closeEvent
        );
        return true;
      },
      onError: (event: WebSocketEventMap["error"]) => {
        console.error("[useAIStream] WebSocket error:", event);
        onStreamError?.(event);
      },
    }
  );

  const processMessage = useCallback(
    (response: AIStreamResponse) => {
      if (response.stage === "starting") {
        setIsStreaming(true);
        setCurrentResponse("");
        responseRef.current = "";
        onStreamStart?.();
      } else if (response.stage === "streaming") {
        responseRef.current += response.content;
        setCurrentResponse(responseRef.current);
      } else if (response.stage === "complete") {
        responseRef.current = response.content;
        setCurrentResponse(response.content);
        setIsStreaming(false);
        onStreamComplete?.(response);
      }
    },
    [onStreamStart, onStreamComplete]
  );

  if (lastJsonMessage) {
    processMessage(lastJsonMessage as AIStreamResponse);
  }

  const sendMessage = useCallback(
    (message: {
      action: string;
      collectionId: string;
      inputMessage: string;
      userId: string;
      employeeId?: string;
      personaId?: string;
    }) => {
      console.log("[useAIStream] Sending message:", message);
      responseRef.current = "";
      setCurrentResponse("");
      sendJsonMessage(message);
    },
    [sendJsonMessage]
  );

  return {
    isStreaming,
    currentResponse,
    sendMessage,
  };
};
